import type { Opportunity, OpportunityFile, Product, Stage } from '../components/OpportunityPageTypes';

/** Aligns API stage labels with the English keys used in the UI (STAGES). */
export function parseStageName(stageName: string): string {
  switch (stageName) {
    case 'Proposta de Valor':
      return 'Value Proposition';
    case 'Quotação':
      return 'Quotation';
    case 'Demonstração':
      return 'Demonstration';
    case 'Negociação':
      return 'Negociation';
    case 'Fechamento':
      return 'Closed';
    default:
      return stageName;
  }
}

/** Maps one `/api/v1/opportunities/:enterpriseId` row to the in-app Opportunity shape. */
export function mapApiRowToOpportunity(opportunity: any): Opportunity {
  const stages: Stage[] = opportunity.stages
    .map((stage: any) => ({
      id: stage.id,
      opportunity_id: stage.opportunity_id,
      name: parseStageName(stage.name),
      created_at: stage.start_date + 'Z',
      finished_at: stage.finish_date,
    }))
    .sort(
      (a: Stage, b: Stage) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );

  return {
    id: opportunity.id,
    name: opportunity.name,
    campaign_id: opportunity.campaign_id,
    client_ids: opportunity.client_ids ?? [],
    contact_id: opportunity.contact_id || '',
    user_id: opportunity.user_id,
    owner: opportunity.owner ?? null,
    value: Number.parseFloat(opportunity.value),
    probability: opportunity.probability,
    decision_maker: opportunity.decision_maker_id,
    influencer: opportunity.influencer_id,
    investidor: opportunity.investidor_id,
    client_image: opportunity.client_image,
    noSalesMade: opportunity.no_sales_made || false,
    remito: opportunity.remito,
    products: opportunity.products.map((product: any) => {
      const [year, month, day] = product.payment?.data_avista?.split('-') || [];
      return {
        productId: product.product_id,
        salesPackageId: product.sales_package_id || undefined,
        salesPackageName: product.sales_package_name || undefined,
        discountType: product.discount_type || undefined,
        discountValue: product.discount_value != null ? Number(product.discount_value) : undefined,
        productName: product.name,
        quantity: product.quantity,
        currency: product.currency,
        price: Number.parseFloat(product.deal_price),
        discount: Number.parseFloat(product.discount),
        sold: product.sold || false,
        payment: {
          tipo: product.payment?.tipo || 'avista',
          data:
            year && month && day
              ? new Date(Number(year), Number(month) - 1, Number(day))
              : null,
          client_id: product.payment?.client_id || undefined,
          parcelas: product.payment?.parcelas?.map((parcela: any) => {
            const [py, pm, pd] = parcela.data?.split('-') || [];
            return {
              data:
                py && pm && pd
                  ? new Date(Number(py), Number(pm) - 1, Number(pd))
                  : null,
              client_id: parcela.client_id || undefined,
              amount:
                parcela.amount != null ? Number.parseFloat(String(parcela.amount)) : undefined,
            };
          }),
        },
      } as Product;
    }),
    stages,
    files: (opportunity.files?.map((file: any) => ({
      id: file.id,
      opportunity_id: file.opportunity_id,
      file_name: file.file_name,
      file: file.file_url,
      created_at: file.upload_date + 'Z',
      size: file.size,
    })) ?? []) as OpportunityFile[],
    startDate: opportunity.startDate + 'Z',
    client: opportunity.client_name,
    discount: Number.parseFloat(opportunity.discount),
    deletedAt: opportunity.deleted_at ?? null,
  };
}
